import { useMemo, useState } from 'react';

/**
 * PLAT-11158 — collapse state for the reel transcript's two levels.
 *
 * Collapsing is reading, not editing: plain React state, never the stored
 * reel's own `chapter.collapsed` (that flag is the Reel Editor's, persisted
 * with the reel) and never the show-only filter channels (the topic and
 * saved-segment filters own those). No store is written anywhere here, so
 * collapsing cannot dirty the clip and cannot appear in undo — by
 * construction, not by discipline.
 *
 * Lifetime is the host's: the clip preview's bridge stays mounted across tab
 * switches (tabs are `display:none`-toggled), so the state survives them and
 * resets when the clip is reopened — exactly the ticket's session semantics,
 * with no persistence code at all.
 */
export type ReelTranscriptCollapseView = {
  collapsedChapterIds: ReadonlySet<string>;
  collapsedSectionIds: ReadonlySet<string>;
  toggleChapter: (chapterId: string) => void;
  toggleSection: (sectionId: string) => void;
  /**
   * PLAT-11159 — open whatever hides this Section, its Chapter included: a
   * Section open inside a folded Chapter is not a renderable state, so the
   * reveal clears its own fold and its Chapter's (the caller names it — the
   * state doesn't know the structure) in one write. For navigation only —
   * search, playback, a restored position, the Reel Editor's jump — never a
   * render.
   */
  revealSection: (
    sectionId: string,
    chapterId?: string,
    /**
     * PLAT-11164 — why the app opened it (playback, search, jump, mount).
     * The base hook ignores it; the host's analytics wrapper reads it.
     */
    reason?: 'playback' | 'search' | 'jump' | 'mount',
  ) => void;
};

const toggled = (ids: ReadonlySet<string>, id: string): Set<string> => {
  const next = new Set(ids);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

export function useReelTranscriptCollapse(): ReelTranscriptCollapseView {
  const [collapsedChapterIds, setCollapsedChapterIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<
    ReadonlySet<string>
  >(new Set());

  // One identity per state change: the context view embeds this object, and
  // its identity is what tells the renderer something folded or unfolded.
  return useMemo(
    () => ({
      collapsedChapterIds,
      collapsedSectionIds,
      toggleChapter: (chapterId: string) =>
        setCollapsedChapterIds(ids => toggled(ids, chapterId)),
      toggleSection: (sectionId: string) =>
        setCollapsedSectionIds(ids => toggled(ids, sectionId)),
      revealSection: (sectionId: string, chapterId?: string) => {
        if (chapterId !== undefined) {
          setCollapsedChapterIds(ids => {
            if (!ids.has(chapterId)) return ids;
            const next = new Set(ids);
            next.delete(chapterId);
            return next;
          });
        }
        setCollapsedSectionIds(ids => {
          if (!ids.has(sectionId)) return ids;
          const next = new Set(ids);
          next.delete(sectionId);
          return next;
        });
      },
    }),
    [collapsedChapterIds, collapsedSectionIds],
  );
}
